import { Node, mergeAttributes } from '@tiptap/core'

/**
 * Een figuur in rich text is een verwijzing, nooit de afbeelding zelf.
 *
 * Dat is een harde repo-afspraak (ontwerp 11.4) en de reden dat dit niet
 * `@tiptap/extension-image` met een `src` is: een base64-`src` zou de bytes in het
 * sectiedocument zetten, en dan groeit dat document met elke figuur, gaat het mee in
 * elke synchronisatie naar elke medeschrijver, en staat hetzelfde bestand twee keer
 * in de opslag zodra iemand het dupliceert. De importroute uit Word is precies waar
 * dat stilzwijgend misgaat.
 *
 * Dit knooppunt draagt daarom alleen wat een `DocumentVerwijzing` in het model ook
 * draagt: de SHA-256, de bestandsnaam en de omvang (U25, R-199). Waar de bytes staan
 * weet de content-addressed opslag, en die komt in M3; tot dan tekent het knooppunt
 * een kader met de naam erin.
 */
export interface FigureAttributes {
  readonly hash: string
  readonly bestandsnaam: string
  readonly grootteBytes: number
}

export const Figure = Node.create({
  name: 'figuur',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      hash: { default: null },
      bestandsnaam: { default: null },
      grootteBytes: { default: null },
    }
  },

  parseHTML() {
    return [{ tag: 'figure[data-hash]' }]
  },

  renderHTML({ HTMLAttributes }) {
    // Geen `img`: er is in M2 geen adres om naartoe te wijzen, en een gebroken
    // afbeelding is voor een invuller een storing. Het kader met de naam zegt wat er
    // hangt; de weergave met de echte bytes komt met de opslag in M3.
    return [
      'figure',
      mergeAttributes(HTMLAttributes, { class: 'ef-figuur', 'data-hash': HTMLAttributes['hash'] }),
      ['figcaption', {}, String(HTMLAttributes['bestandsnaam'] ?? '')],
    ]
  },
})
